import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { StickyNote } from 'lucide-react';
import { kanbanService, KanbanTicket } from '../../services/kanbanService';
import { useToast } from '../../hooks/use-toast';

interface KanbanFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticket: KanbanTicket | null;
}

interface CampoValue {
  campoLabel: string;
  campoTipo: string;
  valor: string;
  opcoes: string | null;
}

export const KanbanFieldDialog = ({ open, onOpenChange, ticket }: KanbanFieldDialogProps) => {
  const [campos, setCampos] = useState<CampoValue[]>([]);
  const [nota, setNota] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && ticket) {
      loadFields();
      setNota('');
    }
  }, [open, ticket]);

  const loadFields = async () => {
    if (!ticket) return;
    setLoading(true);
    try {
      const [defRes, camposRes] = await Promise.all([
        kanbanService.listarDefinicoes(),
        kanbanService.listarCampos(ticket.id),
      ]);

      const definicoes = defRes.data.data || [];
      const valores = camposRes.data.data || [];
      const valorMap: Record<string, string> = {};
      valores.forEach((c: any) => { valorMap[c.campoLabel] = c.valor || ''; });

      setCampos(definicoes.map(d => ({
        campoLabel: d.campoLabel,
        campoTipo: d.campoTipo,
        valor: valorMap[d.campoLabel] || '',
        opcoes: d.opcoes || null,
      })));
    } catch {
      setCampos([]);
    } finally {
      setLoading(false);
    }
  };

  const updateCampo = (campoLabel: string, valor: string) => {
    setCampos(prev => prev.map(c =>
      c.campoLabel === campoLabel ? { ...c, valor } : c
    ));
  };

  const handleSave = async () => {
    if (!ticket) return;
    setSaving(true);
    try {
      const campoPromises = campos
        .filter(c => c.campoLabel.trim())
        .map(c =>
          kanbanService.upsertCampo({
            ticketId: ticket.id,
            campoLabel: c.campoLabel,
            campoTipo: c.campoTipo,
            valor: c.valor || undefined,
            opcoes: c.campoTipo === 'select' ? c.opcoes || undefined : undefined,
          })
        );

      await Promise.all(campoPromises);

      if (nota.trim()) {
        await kanbanService.criarNota({ ticketId: ticket.id, conteudo: nota.trim() });
      }

      toast({ title: 'Sucesso', description: 'Campos salvos com sucesso' });
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: 'Erro',
        description: e.response?.data?.message || 'Erro ao salvar',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] dark:bg-slate-700 max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="dark:text-white">
            Campos do Ticket {ticket?.nome && `— ${ticket.nome}`}
          </DialogTitle>
          <DialogDescription className="dark:text-gray-300">
            Preencha os campos definidos para sua empresa e adicione notas internas.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-sm text-gray-500">
              Carregando campos...
            </div>
          ) : (
            <>
              {campos.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Campos da Empresa
                  </Label>
                  {campos.map(campo => (
                    <div
                      key={campo.campoLabel}
                      className="p-3 border border-gray-200 dark:border-slate-600 rounded-lg bg-yellow-50/50 dark:bg-yellow-900/10 space-y-2"
                    >
                      <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {campo.campoLabel}
                      </Label>
                      {campo.campoTipo === 'textarea' ? (
                        <Textarea
                          value={campo.valor}
                          onChange={(e) => updateCampo(campo.campoLabel, e.target.value)}
                          placeholder={`Valor de ${campo.campoLabel}`}
                          className="h-20 text-sm dark:bg-slate-600 dark:text-white dark:border-slate-500 resize-none"
                        />
                      ) : campo.campoTipo === 'select' && campo.opcoes ? (
                        <div className="space-y-1">
                          <Select
                            value={campo.valor}
                            onValueChange={(v) => updateCampo(campo.campoLabel, v)}
                          >
                            <SelectTrigger className="dark:bg-slate-600 dark:text-white dark:border-slate-500">
                              <SelectValue placeholder="Selecione..." />
                            </SelectTrigger>
                            <SelectContent>
                              {campo.opcoes.split('\n').filter(Boolean).map(opt => (
                                <SelectItem key={opt.trim()} value={opt.trim()}>{opt.trim()}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : campo.campoTipo === 'data' ? (
                        <Input
                          value={campo.valor}
                          onChange={(e) => updateCampo(campo.campoLabel, e.target.value)}
                          type="date"
                          className="h-8 text-sm dark:bg-slate-600 dark:text-white dark:border-slate-500"
                        />
                      ) : (
                        <Input
                          value={campo.valor}
                          onChange={(e) => updateCampo(campo.campoLabel, e.target.value)}
                          type={campo.campoTipo === 'numero' ? 'number' : 'text'}
                          placeholder={
                            campo.campoTipo === 'numero' ? '0' :
                            `Valor de ${campo.campoLabel}`
                          }
                          className="h-8 text-sm dark:bg-slate-600 dark:text-white dark:border-slate-500"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {campos.length === 0 && (
                <div className="text-center py-6 text-sm text-gray-400 dark:text-gray-500">
                  Nenhum campo definido para esta empresa.
                  <br />
                  Peça ao administrador para criar campos em "Gerenciar Campos".
                </div>
              )}

              {/* Nota Interna */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <StickyNote size={12} />
                  Adicionar Nota Interna
                </Label>
                <Textarea
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                  placeholder="Digite sua nota interna aqui..."
                  className="h-24 text-sm dark:bg-slate-600 dark:text-white dark:border-slate-500 resize-none bg-yellow-50/50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800/30"
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter className="border-t border-gray-200 dark:border-slate-600 pt-3">
          <DialogClose asChild>
            <Button variant="secondary" size="sm" className="dark:bg-slate-600 dark:text-white">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
